import { useEffect, useState, useCallback, useMemo } from "react";
import { Tabs, Tab } from "@mui/material";
import { getBrokerPositionBook, getBrokerPositionBookColumns } from "../../api/brokerBooks";
import CustomTable from "../../components/CustomTable/CustomTable";
import BookRefreshBar from "../../components/BookRefreshBar/BookRefreshBar";
import BookTableColumnPrefsInfo from "../../components/BookTableColumnPrefsInfo/BookTableColumnPrefsInfo";
import GlobalIcon from "../../components/GlobalIcon/GlobalIcon";
import BookSquareOffAddMoreFlow from "../../components/BookSquareOffAddMoreFlow/BookSquareOffAddMoreFlow";
import BookMetaCards from "../../components/BookMetaCards/BookMetaCards";
import { BrandLoader } from "../../components/Brand";
import { extractColumnsConfig, resolveBookColumns } from "../../utils/brokerBookColumns";
import { extractBookMetaSummary } from "../../utils/bookMetaSummary";
import {
  POSITION_BOOK_FILTER_OPTIONS,
  filterPositionBookRows,
} from "../../utils/positionBookFilter";
import { TABLE_COLUMN_TABLE_IDS } from "../../utils/tableColumnPreferences";
import { useTableColumnPreferencesVersion } from "../../hooks/useTableColumnPreferences";
import { useIntervalWhen } from "../../hooks/useIntervalWhen";
import styles from "./PositionBookPage.module.css";

function PositionBookPage() {
  const [positionBook, setPositionBook] = useState([]);
  const [metaSummary, setMetaSummary] = useState(null);
  const [apiColumns, setApiColumns] = useState([]);
  const [positionFilter, setPositionFilter] = useState("all");
  useTableColumnPreferencesVersion(TABLE_COLUMN_TABLE_IDS.POSITION_BOOK);
  const columnsConfig = resolveBookColumns(apiColumns, TABLE_COLUMN_TABLE_IDS.POSITION_BOOK);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRowsData, setSelectedRowsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [lastRefreshAt, setLastRefreshAt] = useState(null);
  const [ctaModalOpen, setCtaModalOpen] = useState(false);

  useEffect(() => {
    const fetchColumns = async () => {
      try {
        const response = await getBrokerPositionBookColumns();
        const columns = extractColumnsConfig(response);
        if (columns && columns.length > 0) {
          setApiColumns(columns);
        }
      } catch (err) {
        const errorMessage = err?.response?.data?.detail || err.message || "Failed to fetch columns configuration";
        console.error("Failed to fetch columns config:", errorMessage);
        setError(errorMessage);
      }
    };

    fetchColumns();
  }, []);

  const fetchPositionBook = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setIsLoading(true);
      setError("");
    }

    try {
      const data = await getBrokerPositionBook();
      setPositionBook(data["book_rows"] || []);
      setMetaSummary(extractBookMetaSummary(data));
      setLastRefreshAt(Date.now());
      if (silent) setError("");
    } catch (err) {
      const errorMessage = err?.response?.data?.detail || err.message || "Failed to fetch position book data";
      setError(errorMessage);
      if (!silent) {
        setPositionBook([]);
        setMetaSummary(null);
      }
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchPositionBook();
  }, [fetchPositionBook]);

  // Live refresh: position data only. Pauses while Square Off / Add More is open.
  useIntervalWhen(() => {
    void fetchPositionBook({ silent: true });
  }, 10_000, !ctaModalOpen);

  const handleRowSelect = useCallback((selectedIds, rowsData) => {
    setSelectedRows(selectedIds);
    setSelectedRowsData(rowsData || []);
  }, []);

  const handleExport = useCallback(() => {}, []);

  const filteredPositionBook = useMemo(
    () => filterPositionBookRows(positionBook, positionFilter),
    [positionBook, positionFilter]
  );

  const handlePositionFilterChange = useCallback((event) => {
    setPositionFilter(event.target.value);
  }, []);

  return (
    <div className={styles["container"]}>
      <div className={styles["primary-tabs-container"]}>
        <Tabs value="positions" className={styles["primary-tabs"]}>
          <Tab
            icon={<GlobalIcon iconName="HoldingBookIcon" className={styles["tab-icon"]} />}
            iconPosition="start"
            label="Positions"
            value="positions"
            className={styles["primary-tab"]}
            disableRipple
          />
        </Tabs>
      </div>

      <div className={styles["content-wrapper"]}>
        <BookMetaCards
          summary={metaSummary}
          loading={isLoading && lastRefreshAt === null}
        />

        <BookSquareOffAddMoreFlow
          bookType="position"
          selectedRowsData={selectedRowsData}
          selectedCount={selectedRows.length}
          buttonClassName={styles["action-button"]}
          containerClassName={styles["action-buttons-container"]}
          onCtaModalOpenChange={setCtaModalOpen}
        />

        {error && <div className={styles["error-message"]}>{error}</div>}

        <div className={styles["table-wrapper"]}>
          {columnsConfig.length === 0 || (isLoading && lastRefreshAt === null) ? (
            error && columnsConfig.length === 0 ? null : (
              <BrandLoader variant="block" caption="Loading positions…" />
            )
          ) : (
            <CustomTable
              columns={columnsConfig}
              data={filteredPositionBook}
              onRowSelect={handleRowSelect}
              selectedRows={selectedRows}
              rowKey="key"
              tableId={TABLE_COLUMN_TABLE_IDS.POSITION_BOOK}
              enableSearch={true}
              enableSorting={true}
              enableSelection={true}
              enableShowEntries={true}
              enableFilter={false}
              enableExport={true}
              enablePagination={true}
              searchPlaceholder="Search"
              onExport={handleExport}
              exportType="Positions"
              columnsWithDropdown={["broker_client_code", "exchange_trading_symbol"]}
              toolbarFilterOptions={POSITION_BOOK_FILTER_OPTIONS}
              toolbarFilterValue={positionFilter}
              onToolbarFilterChange={handlePositionFilterChange}
              toolbarRefresh={
                <>
                  <BookTableColumnPrefsInfo />
                  <BookRefreshBar
                    lastRefreshAt={lastRefreshAt}
                    onReload={() => void fetchPositionBook()}
                    isLoading={isLoading}
                  />
                </>
              }
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default PositionBookPage;
